import { removeTrapFocus } from '@theme/focus';

/**
 * Desktop menu row: Horizon measures it again once the row's width has settled (THEME.md §7.1, §11.2; T-59).
 *
 * Horizon's `overflow-list` (in `blocks/_header-menu.liquid`) measures again when the list's own size changes, and its
 * header shows the menu again after a minimum-reached switch to the drawer only when the window grows. From 990 px a
 * theme drawer, such as the cart drawer, narrows the page rather than the window, and closing it slides the page back:
 * - while the row has room, the list is only as wide as its items, so it grows in steps. A step that lands just after
 *   a measurement is the first resize notice after Horizon observes again, which its `ResizeNotifier` skips, and items
 *   stay in "More" although they fit (after the cart drawer at 1440 px: Sale %; at 1100 px: three items);
 * - below about 1,055 px the open cart drawer leaves room for fewer than two items, Horizon switches the header to the
 *   drawer, and with the window unchanged the menu never comes back.
 *
 * This watches the row's width. 150 ms after it last changed, while items are in "More", it asks Horizon to measure
 * again with Horizon's own `reflow` event, as Horizon's product cards do. After the row has grown while Horizon hides
 * the menu (`minimum-reached`), it sends Horizon's `overflowMinimum` event with `minimumReached: false`, as Horizon's
 * header does when the window grows, then has the shown menu measured: back to the drawer if two items still don't
 * fit. With every item in the row nothing happens.
 */

const SETTLE_MS = 150;

/** @type {WeakSet<Element>} */
const watched = new WeakSet();

function watch() {
  for (const menu of document.querySelectorAll('header-menu')) {
    const list = menu.querySelector('overflow-list');
    const row = menu.closest('.header__columns');
    if (!list || !row || watched.has(row)) continue;
    watched.add(row);

    /** @type {number | null} */
    let width = null;
    let grew = false;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;

    const remeasure = () => list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));

    const settle = () => {
      if (!list.isConnected) return;
      if (list.hasAttribute('minimum-reached')) {
        if (grew) {
          list.dispatchEvent(new CustomEvent('overflowMinimum', { bubbles: true, detail: { minimumReached: false } }));
          // Horizon shows the menu in the next frame; measure it after that
          requestAnimationFrame(() => requestAnimationFrame(remeasure));
        }
      } else if (list.querySelector(':scope > [slot="overflow"]') && list.getClientRects().length) {
        remeasure();
      }
      grew = false;
    };

    new ResizeObserver(([entry]) => {
      const next = entry?.contentRect.width;
      if (next === undefined || next === width) return;
      const first = width === null;
      if (!first && next > width) grew = true;
      width = next;
      if (first) return;
      clearTimeout(timer);
      timer = setTimeout(settle, SETTLE_MS);
    }).observe(row);
  }
}

watch();
// The theme editor renders the header again after a change
document.addEventListener('shopify:section:load', watch);

/**
 * The header's exact height (THEME.md §7.1, §11.2; D-106, T-171): `--okt-header-height` on the body, the header's
 * border-box height, unrounded, from a ResizeObserver, so it's measured when the header changes size, never while
 * scrolling. Horizon's `--header-height` is rounded to whole pixels (109 for 108.77), which left a hairline between
 * the header and what sticks under it (`--okt-header-offset` in `snippets/okt-tokens.liquid`).
 */
const headerSize = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const height = entry.borderBoxSize?.[0]?.blockSize;
    if (height && entry.target.isConnected) document.body.style.setProperty('--okt-header-height', `${height}px`);
  }
});

function watchHeaderHeight() {
  const header = document.querySelector('#header-component');
  if (header) headerSize.observe(header);
}

watchHeaderHeight();
document.addEventListener('shopify:section:load', watchHeaderHeight);

/**
 * The menu drawer under the header (THEME.md §7.1, §11.2; T-146). The drawer and its page dim start at the header's
 * bottom edge (`snippets/okt-mobile-menu-position.liquid` reads `--okt-menu-drawer-top`), so the header stays in view
 * while the drawer is open, its menu button turned into Horizon's X:
 * - as the drawer opens (its `details` gets `open`), and on a resize while it is open, this measures the header's
 *   bottom edge: below the header's top row on a page at the top, at the sticky header's bottom once scrolled. With
 *   Sticky On scroll up the open drawer keeps the header pinned and in view (okt-tokens; D-106), also on a page
 *   scrolled less than the header's height;
 * - the X closes the drawer with its slide: Horizon's `toggle()` closes it, and the summary's native toggle, which would
 *   hide the drawer at once, is cancelled;
 * - a tap elsewhere in the header (search, account, cart, the logo) closes the drawer, then does its own job, so one
 *   overlay shows at a time and the scroll lock passes on. Horizon's focus trap is lifted on the press, so focus lands
 *   on that control and the cart or search returns it there;
 * - once the drawer has closed, focus goes back to the menu button if it was inside the drawer or on the page (Horizon
 *   leaves it on the page), unless another header control took over.
 */
const MENU = '#header-component header-drawer > details';

/** @returns {HTMLDetailsElement | null} */
const menu = () => document.querySelector(MENU);

/** Set when another header control closes the drawer: focus stays with that control */
let handingOver = false;

function placeDrawer() {
  const header = document.querySelector('#header-component');
  if (!header) return;
  const bottom = Math.max(0, header.getBoundingClientRect().bottom);
  document.documentElement.style.setProperty('--okt-menu-drawer-top', `${bottom}px`);

  // On scroll up the open drawer pins the header (okt-tokens; D-106, T-171): if it's still sliding into place, the
  // drawer goes under it again once it's there
  const slide = header.parentElement
    ?.getAnimations?.()
    .find((animation) => animation instanceof CSSTransition && animation.transitionProperty === 'top');
  slide?.finished.then(placeDrawer, () => {});
}

/** @param {HTMLDetailsElement} details */
function restoreFocus(details) {
  if (handingOver) {
    handingOver = false;
    return;
  }
  const summary = details.querySelector(':scope > summary');
  const active = document.activeElement;
  const lost = !active || active === document.body || (details.contains(active) && active !== summary);
  if (lost && summary instanceof HTMLElement) summary.focus({ preventScroll: true });
}

/** @type {WeakSet<Element>} */
const observed = new WeakSet();

function watchDrawer() {
  const details = menu();
  if (!details || observed.has(details)) return;
  observed.add(details);

  let open = details.open;
  if (open) placeDrawer();

  new MutationObserver(() => {
    if (details.open === open) return;
    open = details.open;
    if (open) {
      handingOver = false;
      placeDrawer();
    } else {
      restoreFocus(details);
    }
  }).observe(details, { attributes: true, attributeFilter: ['open'] });
}

/**
 * @param {Event} event
 * @returns {HTMLDetailsElement | null} The open drawer's `details`, when the event lands in the header outside it
 */
function outsideInHeader(event) {
  const details = menu();
  const { target } = event;
  if (!details?.open || !details.classList.contains('menu-open') || !(target instanceof Element)) return null;
  return target.closest('#header-component') && !details.contains(target) ? details : null;
}

document.addEventListener(
  'pointerdown',
  (event) => {
    if (outsideInHeader(event)) removeTrapFocus();
  },
  true
);

// On the window, so it runs before Horizon's handlers (delegated on the document, capture): under reduced motion
// Horizon's close has nothing to wait for and ends at once, and the summary's own toggle then opened the details
// again, unseen, so the drawer couldn't open any more (T-171)
window.addEventListener(
  'click',
  (event) => {
    const details = menu();
    if (!details?.open || !(event.target instanceof Element)) return;

    if (details.querySelector(':scope > summary')?.contains(event.target)) {
      event.preventDefault();
      return;
    }

    if (outsideInHeader(event)) {
      handingOver = true;
      removeTrapFocus();
      /** @type {(HTMLElement & { close?: () => void }) | null} */ (details.closest('header-drawer'))?.close?.();
    }
  },
  true
);

window.addEventListener('resize', () => {
  if (menu()?.open) placeDrawer();
});

watchDrawer();
document.addEventListener('shopify:section:load', watchDrawer);
